import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AlertCircle } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { api } from "@/lib/api";

const TITLES = {
  terms: "Terms and Conditions of Sale",
  refunds: "Cancellation and Refund Policy",
  delivery: "Delivery and Collection Policy",
  allergens: "Allergen and Food Information Policy",
  privacy: "Privacy Notice",
  cookies: "Cookie Policy",
};

const PUBLIC_PATHS = {
  terms: "terms",
  refunds: "cancellation-refunds",
  delivery: "delivery-collection",
  allergens: "allergen-information",
  privacy: "privacy",
  cookies: "cookies",
};

export default function Legal({ kind }) {
  const [state, setState] = useState({ status: "loading", data: null });

  useEffect(() => {
    setState({ status: "loading", data: null });
    api.get(`/public/legal/${kind}`)
      .then((r) => setState({ status: "ok", data: r.data }))
      .catch((e) => setState({ status: e?.response?.status === 404 ? "unpublished" : "error", data: null }));
  }, [kind]);

  const title = state.data?.title || TITLES[kind] || "Legal";

  useEffect(() => {
    document.title = `${title} — Bakes by CR`;
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.rel = "canonical";
      document.head.appendChild(canonical);
    }
    canonical.href = `https://www.bakesbycr.co.uk/legal/${PUBLIC_PATHS[kind] || kind}`;
    let robots = document.querySelector('meta[name="robots"]');
    if (state.status !== "ok") {
      if (!robots) {
        robots = document.createElement("meta");
        robots.name = "robots";
        document.head.appendChild(robots);
      }
      robots.content = "noindex";
    } else if (robots) {
      robots.remove();
    }
  }, [kind, title, state.status]);

  if (state.status === "loading") {
    return <div className="mx-auto max-w-3xl px-4 py-16 text-sm text-cream-muted">Loading…</div>;
  }

  if (state.status === "unpublished" || state.status === "error") {
    return (
      <div className="mx-auto max-w-3xl px-4 py-16 text-center sm:px-6" data-testid={`legal-unpublished-${kind}`}>
        <h1 className="font-serif text-3xl font-bold text-cream-text">{title}</h1>
        <p className="mt-4 text-sm leading-relaxed text-cream-muted">
          This page is being finalised and will be available shortly. If you have a question in the meantime,
          please contact us — details are in the footer.
        </p>
        <Link to="/" className="mt-6 inline-block rounded-full bg-gold px-6 py-3 text-sm font-bold text-choco hover:bg-gold-hover" data-testid="legal-home-link">
          Back to Bakes by CR
        </Link>
      </div>
    );
  }

  const updated = state.data.updated_at
    ? new Date(state.data.updated_at).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric", timeZone: "Europe/London" })
    : null;

  return (
    <div className="mx-auto max-w-3xl px-4 py-12 sm:px-6 sm:py-16 lg:px-8" data-testid={`${kind}-page`}>
      <Reveal>
        <h1 className="font-serif text-3xl font-bold tracking-tight text-cream-text sm:text-4xl">{title}</h1>
        {updated && <p className="mt-2 text-xs text-cream-muted">Last updated: {updated}</p>}
      </Reveal>
      <div className="mt-10 space-y-8">
        {state.data.sections.map((s, i) => (
          <Reveal key={i} delay={i * 0.03}>
            <section>
              <h2 className="font-serif text-xl font-semibold text-cream-text">{s.heading}</h2>
              <p className="mt-2 whitespace-pre-line text-sm leading-relaxed text-cream-muted">{s.body}</p>
            </section>
          </Reveal>
        ))}
      </div>
      <Reveal delay={0.1}>
        <div className="mt-12 flex items-start gap-3 rounded-2xl border border-choco-line bg-choco-light p-4" data-testid="legal-questions-note">
          <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-gold" aria-hidden="true" />
          <p className="text-sm leading-relaxed text-cream-muted">
            Questions about this page? Contact us using the details in the footer and we'll be happy to help.
          </p>
        </div>
      </Reveal>
    </div>
  );
}
